// src/reports/InventoryDashboardReport.jsx — Reporting → Location Reports →
// Inventory Dashboard (Eric, 2026-09-22). OpsHub-native replacement for the
// Grafana "Teams Las Vegas Inventory" dashboard: one Teams / Warehouse
// location, every SKU stocked there from the nightly inventory snapshot, low-stock panels
// on a threshold, stock-level tiers, top committed, full table with Units
// Sold (live, from the order cache). Refresh now = the Zero Sales snapshot.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, BlockStack, InlineStack, InlineGrid, Text, Button, ButtonGroup, TextField, Banner, Badge, Checkbox } from '@shopify/polaris';
import { useAuthFetch, useAuth } from '../AuthComponent';
import { isoDaysAgo, todayIso, ReportDateRange, ReportPicker } from '../Reporting';

import { hasRole } from '../constants/roles';
const PAGE = 200;
const PANEL_LIMIT = 50;
const fmtN = n => (n === null || n === undefined) ? '—' : Number(n).toLocaleString();
const fmtUSD = n => (n === null || n === undefined || Number.isNaN(Number(n))) ? '—' : Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
function fmtRun(ranAt) {
    if (!ranAt) return 'never';
    const d = new Date(ranAt);
    return `${d.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' })} ${d.toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'numeric', day: 'numeric', year: 'numeric' })}`;
}
const THRESHOLDS = ['5', '10', '15', '25'];
const TIER_OPTIONS = [{ label: 'All stock levels', value: '' }, { label: 'Out of Stock (0)', value: 'out_of_stock' }, { label: 'Critical (1–10)', value: 'critical' },
    { label: 'Low (11–19)', value: 'low' }, { label: 'Moderate (20–34)', value: 'moderate' }, { label: 'Good (35–99)', value: 'good' }, { label: 'Healthy (100+)', value: 'healthy' }];
const OVERSELL_OPTIONS = [{ label: 'Oversell: all', value: '' }, { label: 'Oversell: yes', value: 'yes' }, { label: 'Oversell: no', value: 'no' }];
const PRESET_OPTIONS = [{ label: 'All SKUs', value: '' }, { label: 'Low stock — no incoming', value: 'low_no_incoming' }, { label: 'Low stock — incoming', value: 'low_incoming' }];
// Grafana's colour steps, reproduced (background tints readable on white).
const availShade = (v, panel) => {
    const n = Number(v || 0);
    if (panel === 'no_incoming') return n < 3 ? '#fde2e1' : n < 6 ? '#fdebd3' : '#fdf6d4';
    if (panel === 'incoming')    return n < 5 ? '#fdebd3' : '#fdf6d4';
    return n < 5 ? '#fde2e1' : n < 10 ? '#fdebd3' : n < 25 ? '#fdf6d4' : '#e3f3ea';
};
const committedShade = v => (Number(v || 0) >= 50 ? '#fde2e1' : Number(v || 0) >= 10 ? '#fdebd3' : 'transparent');
// Tight cells: the panels share a row two-up (Eric, 2026-09-22), so every px counts.
const cell = { padding: '4px 6px', whiteSpace: 'nowrap' };
const num = { ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
const mono = { ...cell, fontFamily: 'monospace', fontSize: 12 };
// Panel title cells truncate (full text on hover) so the number columns stay
// in view when two panels share a row (Eric, 2026-09-22).
// width:100% + maxWidth:0 = take whatever is left after the fixed columns and truncate there.
const titleCell = { ...cell, width: '100%', maxWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' };
const titleOf = r => `${r.product_title || ''}${r.variant_title && r.variant_title !== 'Default Title' ? ` — ${r.variant_title}` : ''}`;
// Long SKUs (SPNSR-Scheels-717-Dallas…) truncate inside the link; full SKU on hover.
const SkuLink = ({ sku, onClick }) => (
    <Button variant="plain" onClick={onClick}>
        <span title={sku || ''} style={{ display: 'inline-block', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'bottom', fontFamily: 'monospace', fontSize: 12 }}>{sku || '—'}</span>
    </Button>
);

const COLUMNS = [
    { key: 'sku', label: 'SKU' }, { key: 'product', label: 'Product' }, { key: 'variant', label: 'Variant' }, { key: 'vendor', label: 'Vendor' },
    { key: 'cost', label: 'Cost', right: true }, { key: 'unavailable', label: 'Unavailable', right: true }, { key: 'committed', label: 'Committed', right: true },
    { key: 'available', label: 'Available', right: true }, { key: 'on_hand', label: 'On Hand', right: true }, { key: 'incoming', label: 'Incoming', right: true },
    { key: 'units_sold', label: 'Units Sold', right: true, tip: 'Units on non-cancelled orders placed in the date range with their fulfillment at this location (live, from the order cache)' },
    { key: 'bin', label: 'Bin', singleLocationOnly: true }, { key: 'oversell', label: 'Oversell' },
    // 2026-09-23 (Eric): with several locations a row is the union of that
    // variant across them, so the bin (per location) drops out and this says
    // how many sites it sits at — hover for their names.
    { key: 'locations', label: 'Locations', right: true, multiLocationOnly: true,
      tip: 'How many of the selected locations stock this SKU. Hover the number for their names.' },
];

function useDebounced(value, ms) {
    const [v, setV] = useState(value);
    useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
    return v;
}

function Tile({ label, value, tone, caption }) {
    const bg = { ok: '#e3f3ea', info: '#e6edfb', warn: '#fdebd3', bad: '#fde2e1', muted: '#f1f2f4' }[tone || 'muted'];
    return (
        <div style={{ background: bg, borderRadius: 8, padding: '10px 14px', minWidth: 130, flex: '1 1 130px' }}>
            <div style={{ fontSize: 24, fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>{value}</div>
            <div style={{ fontSize: 12, color: '#6d7175', textTransform: 'uppercase', letterSpacing: '.04em', marginTop: 2 }}>{label}</div>
            {caption ? <div style={{ fontSize: 11, color: '#6d7175' }}>{caption}</div> : null}
        </div>
    );
}

export default function InventoryDashboardReport() {
    const fetchAuth = useAuthFetch();
    const { user } = useAuth();
    const { storeSlug } = useParams();
    const storeKey = storeSlug || 'soccerpost';
    const [searchParams, setSearchParams] = useSearchParams();
    const isAdmin = hasRole(user, ['admin']);

    const [meta, setMeta] = useState({ locations: [], last_run: null, refreshing: false });
    // 2026-09-23 (Eric): several locations and several vendors. `location` (single)
    // is still read so older links keep working; new links use repeated `loc`.
    const [locIds, setLocIds] = useState(() => {
        const many = searchParams.getAll('loc').filter(Boolean);
        const one = searchParams.get('location');
        return many.length ? many : (one ? [one] : []);
    });
    const [threshold, setThreshold] = useState(THRESHOLDS.includes(searchParams.get('threshold')) ? searchParams.get('threshold') : '25');
    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || isoDaysAgo(29));
    const [dateTo, setDateTo] = useState(searchParams.get('to') || todayIso());
    const [product, setProduct] = useState(searchParams.get('product') || '');
    const [sku, setSku] = useState(searchParams.get('sku') || '');
    const [vendors, setVendors] = useState(() => {
        const many = searchParams.getAll('vendor').filter(Boolean);
        return many;
    });
    // How low stock / oversold are judged across several locations: the combined
    // total, or any single location under the threshold (Eric, 2026-09-23).
    const [basis, setBasis] = useState(searchParams.get('basis') === 'any' ? 'any' : 'combined');
    // 2026-10-28 (Eric): "Inventory not tracked" items (e.g. every Number product) are left out unless ticked.
    const [untracked, setUntracked] = useState(searchParams.get('untracked') === '1');
    const untrackedQ = untracked ? '1' : '';
    const [tier, setTier] = useState(searchParams.get('tier') || '');
    const [oversell, setOversell] = useState(searchParams.get('oversell') || '');
    const [preset, setPreset] = useState(searchParams.get('preset') || '');
    const [sort, setSort] = useState({ key: searchParams.get('sort') || 'available', dir: searchParams.get('dir') === 'desc' ? 'desc' : 'asc' });
    const [page, setPage] = useState(1);
    const [summary, setSummary] = useState(null);
    const [panels, setPanels] = useState({ low_no_incoming: [], low_incoming: [], top_committed: [] });
    const [data, setData] = useState({ rows: [], total: 0, shopify_product_base: '' });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const productQ = useDebounced(product, 400);
    const skuQ = useDebounced(sku, 400);
    const pollRef = useRef(null);
    const tableRef = useRef(null);

    const loadMeta = useCallback(async () => {
        const r = await fetchAuth(`/api/reports/inventory-dashboard/locations?store=${storeKey}`);
        const j = await r.json();
        if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
        setMeta(j);
        // 2026-09-22 (Eric): the URL / dropdown key on the Shopify location id
        // (like every other report), not the internal row id. An old
        // `?location=<row id>` link is upgraded in place.
        const locs = j.locations || [];
        // keep only ids this store actually has; upgrade legacy row ids in place
        const valid = new Set(locs.map(l => String(l.shopify_location_id)));
        const byRow = new Map(locs.map(l => [String(l.location_id), String(l.shopify_location_id)]));
        setLocIds(prev => {
            const next = prev.map(id => valid.has(String(id)) ? String(id) : byRow.get(String(id)))
                             .filter(Boolean);
            if (!next.length && locs.length === 1) return [String(locs[0].shopify_location_id)];
            return next.join('|') === prev.join('|') ? prev : next;
        });
    }, [fetchAuth, storeKey]);

    const loadSummaryAndPanels = useCallback(async () => {
        if (!locIds.length) { setSummary(null); setPanels({ low_no_incoming: [], low_incoming: [], top_committed: [] }); return; }
        const base = new URLSearchParams({ store: storeKey, threshold, date_from: dateFrom, date_to: dateTo, product: productQ, sku: skuQ, basis, untracked: untrackedQ });
        locIds.forEach(id => base.append('locations', id));
        const [s, a, b, c] = await Promise.all([
            fetchAuth(`/api/reports/inventory-dashboard/summary?${base}`),
            fetchAuth(`/api/reports/inventory-dashboard/rows?${base}&preset=low_no_incoming&page_size=${PANEL_LIMIT}`),
            fetchAuth(`/api/reports/inventory-dashboard/rows?${base}&preset=low_incoming&page_size=${PANEL_LIMIT}`),
            fetchAuth(`/api/reports/inventory-dashboard/rows?${base}&preset=top_committed`),
        ]);
        const js = await s.json();
        if (!s.ok) throw new Error(js.detail || `HTTP ${s.status}`);
        setSummary(js);
        const [ja, jb, jc] = await Promise.all([a.json(), b.json(), c.json()]);
        setPanels({ low_no_incoming: ja.rows || [], low_no_incoming_total: ja.total || 0,
                    low_incoming: jb.rows || [], low_incoming_total: jb.total || 0, top_committed: jc.rows || [] });
    }, [fetchAuth, storeKey, locIds, threshold, dateFrom, dateTo, productQ, skuQ, basis, untracked]);

    const loadRows = useCallback(async () => {
        if (!locIds.length) { setData({ rows: [], total: 0, shopify_product_base: '' }); return; }
        setBusy(true); setErr(null);
        try {
            const q = new URLSearchParams({ store: storeKey, threshold, date_from: dateFrom, date_to: dateTo, preset,
                product: productQ, sku: skuQ, tier, oversell, basis, untracked: untrackedQ, sort: sort.key, dir: sort.dir, page: String(page), page_size: String(PAGE) });
            locIds.forEach(id => q.append('locations', id));
            vendors.forEach(v => q.append('vendors', v));
            const r = await fetchAuth(`/api/reports/inventory-dashboard/rows?${q}`);
            const j = await r.json();
            if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
            setData(j);
        } catch (e) { setErr(e.message); }
        finally { setBusy(false); }
    }, [fetchAuth, storeKey, locIds, threshold, dateFrom, dateTo, preset, productQ, skuQ, vendors, tier, oversell, basis, untracked, sort, page]);

    useEffect(() => { loadMeta().catch(e => setErr(e.message)); }, [loadMeta]);
    useEffect(() => { loadSummaryAndPanels().catch(e => setErr(e.message)); }, [loadSummaryAndPanels]);
    useEffect(() => { loadRows(); }, [loadRows]);
    useEffect(() => { setPage(1); }, [locIds, threshold, dateFrom, dateTo, preset, productQ, skuQ, vendors, tier, oversell, basis, untracked, sort]);
    useEffect(() => {
        const p = new URLSearchParams(searchParams);
        const set = (k, v, dflt = '') => { if (v && v !== dflt) p.set(k, v); else p.delete(k); };
        // repeated params must be cleared before re-adding, or every render
        // appends another copy (?loc=A&loc=B&loc=A&loc=B…)
        p.delete('loc'); p.delete('location'); p.delete('vendor');
        locIds.forEach(id => p.append('loc', id));
        set('threshold', threshold, '25'); set('from', dateFrom); set('to', dateTo);
        set('product', product); set('sku', sku); set('tier', tier); set('oversell', oversell); set('preset', preset);
        vendors.forEach(v => p.append('vendor', v));
        set('basis', basis, 'combined');
        set('untracked', untrackedQ);
        set('sort', sort.key, 'available'); set('dir', sort.dir, 'asc');
        if (p.toString() !== searchParams.toString()) setSearchParams(p, { replace: true });
    }, [locIds, threshold, dateFrom, dateTo, product, sku, vendors, tier, oversell, basis, untracked, preset, sort]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {   // poll while a refresh runs, then reload everything
        if (!meta.refreshing) { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } return; }
        if (pollRef.current) return;
        pollRef.current = setInterval(async () => {
            try { const j = await loadMeta(); if (!j.refreshing) { clearInterval(pollRef.current); pollRef.current = null; loadSummaryAndPanels(); loadRows(); } }
            catch (_e) { /* keep polling */ }
        }, 10000);
        return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
    }, [meta.refreshing, loadMeta, loadSummaryAndPanels, loadRows]);

    const refreshNow = async () => {
        setErr(null);
        const r = await fetchAuth('/api/reports/inventory-dashboard/refresh', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ store: storeKey }) });
        const j = await r.json();
        if (!r.ok) { setErr(j.detail || `HTTP ${r.status}`); return; }
        setMeta(m => ({ ...m, refreshing: true }));
    };
    const exportCsv = () => {
        const q = new URLSearchParams({ store: storeKey, threshold, date_from: dateFrom, date_to: dateTo, preset,
            product: productQ, sku: skuQ, tier, oversell, basis, untracked: untrackedQ, sort: sort.key, dir: sort.dir });
        locIds.forEach(id => q.append('locations', id));
        vendors.forEach(v => q.append('vendors', v));
        fetchAuth(`/api/reports/inventory-dashboard/export.csv?${q}`).then(async r => {
            if (!r.ok) {
                const text = await r.text().catch(() => '');
                let msg = text;
                try { msg = JSON.parse(text).detail || text; } catch (_e) { /* not JSON — use raw text */ }
                setErr(msg || `HTTP ${r.status}`);
                return;
            }
            const b = await r.blob();
            const a = document.createElement('a'); a.href = URL.createObjectURL(b); a.download = `inventory_${storeKey}_${locIds.length === 1 ? locIds[0] : locIds.length + '-locations'}.csv`; a.click(); URL.revokeObjectURL(a.href);
        }).catch(e => setErr(e.message));
    };
    const toggleSort = key => setSort(s => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: ['sku', 'product', 'variant', 'vendor', 'bin'].includes(key) ? 'asc' : 'desc' });
    const focusSku = s => { setPreset(''); setTier(''); setSku(s); setProduct(''); tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); };
    const showAll = p => { setPreset(p); setTier(''); tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); };
    const pickTier = t => { setTier(prev => prev === t ? '' : t); setPreset(''); tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); };

    const locOptions = useMemo(() => meta.locations.map(l => ({ label: l.name, value: String(l.shopify_location_id) })), [meta.locations]);
    const vendorOptions = useMemo(() => {
        const vs = new Set([...(panels.low_no_incoming || []), ...(panels.low_incoming || []), ...(data.rows || [])].map(r => r.vendor).filter(Boolean));
        vendors.forEach(v => vs.add(v));
        return [...vs].sort().map(v => ({ label: v, value: v }));
    }, [panels, data.rows, vendors]);
    const lastRun = meta.last_run;
    const pages = Math.max(1, Math.ceil((data.total || 0) / PAGE));
    const maxTier = Math.max(1, ...((summary?.tiers || []).map(t => t.count)));
    const locNames = locIds.map(id => meta.locations.find(l => String(l.shopify_location_id) === String(id))?.name).filter(Boolean);
    const locName = locNames.length === 1 ? locNames[0] : `${locNames.length} locations`;
    const multiLoc = locIds.length > 1;
    const visibleColumns = useMemo(
        () => COLUMNS.filter(c => (c.singleLocationOnly ? !multiLoc : true) && (c.multiLocationOnly ? multiLoc : true)),
        [multiLoc]);
    const prodLink = r => (data.shopify_product_base && r.product_id
        ? <a href={`${data.shopify_product_base}${String(r.product_id).split('/').pop()}`} target="_blank" rel="noopener noreferrer">{r.product_title || '—'}</a>
        : (r.product_title || '—'));
    const oversellBadge = r => (r.oversell ? <Badge tone="attention">Oversell</Badge> : '');

    const LowTable = ({ rows, total, panel, withIncoming }) => (
        <table className="report-table" style={{ width: '100%', borderCollapse: 'collapse', whiteSpace: 'nowrap', fontSize: 13 }}>
            <thead><tr>
                <th style={cell}>SKU</th><th style={cell}>Title</th><th style={num}>Cost</th><th style={num}>Available</th><th style={num}>Committed</th><th style={num}>On Hand</th>
                {withIncoming && <th style={num}>Incoming</th>}<th style={cell}>Oversell</th>
            </tr></thead>
            <tbody>
                {rows.map(r => (
                    <tr key={r.sku || r.variant_id} style={{ borderBottom: '1px solid #f1f2f3' }}>
                        <td style={cell}><SkuLink sku={r.sku} onClick={() => focusSku(r.sku)} /></td>
                        <td style={titleCell} title={titleOf(r)}>{titleOf(r)}</td>
                        <td style={num}>{fmtUSD(r.unit_cost)}</td>
                        <td style={{ ...num, background: availShade(r.available, panel), fontWeight: 600 }}>{fmtN(r.available)}</td>
                        <td style={num}>{fmtN(r.committed)}</td><td style={num}>{fmtN(r.on_hand)}</td>
                        {withIncoming && <td style={{ ...num, background: r.incoming > 0 ? '#e0f2fb' : 'transparent' }}>{fmtN(r.incoming)}</td>}
                        <td style={cell}>{oversellBadge(r)}</td>
                    </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={withIncoming ? 8 : 7} style={{ padding: 16, textAlign: 'center', color: '#6d7175' }}>Nothing below the threshold.</td></tr>}
            </tbody>
            {total > rows.length && <tfoot><tr><td colSpan={withIncoming ? 8 : 7} style={cell}><Button variant="plain" onClick={() => showAll(panel === 'no_incoming' ? 'low_no_incoming' : 'low_incoming')}>Show all {fmtN(total)} in the table below</Button></td></tr></tfoot>}
        </table>
    );

    // 2026-10-28 (Eric): this report runs on ONE store; under All Stores it says so plainly instead of loading.
    if (storeKey === 'all') {
        return (
            <Banner tone="critical" title="Inventory Dashboard can't run across multiple stores">
                <p>It reads one store's inventory snapshot at a time. Pick a single store (Soccer Post, Surf, …) in the store menu at the top left.
                   To compare stock and sales across every store, use Location Reports → Cross Site Sales.</p>
            </Banner>
        );
    }
    return (
        <BlockStack gap="400">
            <Card>
                <BlockStack gap="200">
                    <InlineStack align="space-between" blockAlign="center" wrap>
                        <Text as="h2" variant="headingMd">Inventory Dashboard{locName ? ` — ${locName}` : ''}</Text>
                        <InlineStack gap="200" blockAlign="center">
                            <Text as="span" tone="subdued">Inventory as of {fmtRun(lastRun?.ran_at)}{lastRun?.duration_s ? ` (snapshot took ${Math.round(lastRun.duration_s)} s)` : ''}{lastRun?.status === 'failed' ? ' — last run failed' : ''}</Text>
                            {isAdmin && <Button onClick={refreshNow} disabled={!!meta.refreshing} loading={!!meta.refreshing}>Refresh now</Button>}
                            <Button onClick={exportCsv} disabled={!data.total}>Export CSV</Button>
                        </InlineStack>
                    </InlineStack>
                    <Text as="p" tone="subdued">
                        Every SKU stocked at the location from the nightly Shopify snapshot (4:30 AM ET, or Refresh now): available, on hand, committed, incoming and
                        unavailable (on hand − available − committed). Draft and archived products are not counted, nor are items set to "Inventory not tracked" in Shopify (e.g. every Number product) unless "Include untracked inventory" is ticked. Low stock = available below the threshold. Units Sold is live: units on non-cancelled orders placed in the
                        date range whose fulfillment sits at this location. Oversell = the variant allows selling below zero. Bin = the nightly bin snapshot.
                    </Text>
                    {lastRun?.status === 'failed' && <Banner tone="warning">The last inventory snapshot failed: {lastRun.error}. Showing the previous snapshot.</Banner>}
                    {err && <Banner tone="critical" onDismiss={() => setErr(null)}>{err}</Banner>}
                    <InlineStack gap="300" blockAlign="center" wrap>
                        <ReportPicker label="Location" options={locOptions} value={locIds} onChange={setLocIds} multiple searchable />
                        <ReportPicker label="Low-stock threshold" options={THRESHOLDS.map(t => ({ label: `Available < ${t}`, value: t }))} value={threshold} onChange={setThreshold} />
                        {/* 2026-09-22 (Eric): product / SKU filter at the top — narrows the tiles, the panels and the table together. */}
                        <div style={{ minWidth: 220 }}><TextField label="Product" labelHidden placeholder="Product name…" value={product} onChange={setProduct} autoComplete="off" clearButton onClearButtonClick={() => setProduct('')} /></div>
                        <div style={{ minWidth: 180 }}><TextField label="SKU" labelHidden placeholder="SKU…" value={sku} onChange={setSku} autoComplete="off" clearButton onClearButtonClick={() => setSku('')} /></div>
                        <Checkbox label="Include untracked inventory" checked={untracked} onChange={setUntracked}
                                  helpText={'Items set to "Inventory not tracked" in Shopify, e.g. Number products'} />
                    </InlineStack>
                    <InlineStack gap="200" blockAlign="center" wrap>
                        <Text as="span" tone="subdued">Units sold</Text>
                        <ReportDateRange dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} presets={['7d', '30d', '90d', 'ytd']} />
                    </InlineStack>
                </BlockStack>
            </Card>

            {!locIds.length && <Card><Text as="p" tone="subdued">Pick one or more locations to load their inventory.</Text></Card>}

            {locIds.length > 0 && summary && (<>
                <Card>
                    <BlockStack gap="300">
                        <InlineStack gap="200" wrap>
                            <Tile label="SKUs" value={fmtN(summary.skus)} tone="ok" />
                            <Tile label="On Hand" value={fmtN(summary.on_hand)} tone="info" />
                            <Tile label="Available" value={fmtN(summary.available)} tone="info" />
                            <Tile label="Committed" value={fmtN(summary.committed)} tone="warn" />
                            <Tile label="Incoming" value={fmtN(summary.incoming)} tone="info" />
                            <Tile label="Last updated" value={fmtRun(summary.last_run?.ran_at)} tone="muted" />
                        </InlineStack>
                        <InlineStack gap="200" wrap>
                            <Tile label={`Low stock (< ${threshold})`} value={fmtN(summary.low)} tone="bad" />
                            <Tile label="Low, incoming on the way" value={fmtN(summary.low_incoming)} tone="warn" />
                            <Tile label="Low, no incoming" value={fmtN(summary.low_no_incoming)} tone="bad" />
                            <Tile label="Oversold (available < 0)" value={fmtN(summary.oversold)} tone="warn" caption="personalisation SKUs selling below zero" />
                            <Tile label="Inventory value (cost × on hand)" value={fmtUSD(summary.inventory_value)} tone="ok"
                                  caption={summary.rows_without_cost ? `${fmtN(summary.rows_without_cost)} SKUs have no cost in Shopify` : null} />
                        </InlineStack>
                    </BlockStack>
                </Card>

                {/* 2026-09-22 (Eric): two rows of two — the low-stock pair, then
                    stock-level tiers beside Top 15 committed; one column on phones. */}
                {/* 2026-09-23 (Eric): with several locations, "low" can mean the
                    combined total or any single site being under — the choice sits
                    here, on the boxes it decides, and it also drives the low-stock
                    and oversold tiles above so the numbers agree. */}
                {multiLoc && (
                    <InlineStack gap="200" blockAlign="center" wrap>
                        <Text as="span" tone="subdued">Low stock counts</Text>
                        <ButtonGroup variant="segmented">
                            <Button pressed={basis === 'combined'} onClick={() => setBasis('combined')}>
                                Combined across {locIds.length} locations
                            </Button>
                            <Button pressed={basis === 'any'} onClick={() => setBasis('any')}>
                                Any one location
                            </Button>
                        </ButtonGroup>
                        <Text as="span" tone="subdued" variant="bodySm">
                            {basis === 'combined'
                                ? 'A SKU counts as low when the total across these locations is under the threshold.'
                                : 'A SKU counts as low when any single location is under the threshold — 20 at each of two sites is low at both.'}
                        </Text>
                    </InlineStack>
                )}
                <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
                    <Card>
                        <BlockStack gap="200">
                            <Text as="h3" variant="headingSm">⚠️ Low Stock — Available &lt; {threshold} (no incoming)</Text>
                            <Text as="p" tone="subdued">Running low with nothing on order. These need attention.</Text>
                            <div style={{ overflowX: 'auto' }}><LowTable rows={panels.low_no_incoming} total={panels.low_no_incoming_total || 0} panel="no_incoming" withIncoming={false} /></div>
                        </BlockStack>
                    </Card>
                    <Card>
                        <BlockStack gap="200">
                            <Text as="h3" variant="headingSm">🚚 Low Stock — Available &lt; {threshold} (incoming on the way)</Text>
                            <Text as="p" tone="subdued">Running low but stock is on order. Relief is coming.</Text>
                            <div style={{ overflowX: 'auto' }}><LowTable rows={panels.low_incoming} total={panels.low_incoming_total || 0} panel="incoming" withIncoming /></div>
                        </BlockStack>
                    </Card>
                </InlineGrid>
                <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
                    <Card>
                        <BlockStack gap="200">
                            <Text as="h3" variant="headingSm">📊 Inventory by Stock Level</Text>
                            <Text as="p" tone="subdued">SKUs by available quantity. Click a tier to filter the table.</Text>
                            <div style={{ display: 'grid', gap: 6 }}>
                                {(summary.tiers || []).map(t => (
                                    <button key={t.tier} type="button" onClick={() => pickTier(t.tier)}
                                            style={{ display: 'grid', gridTemplateColumns: '150px 1fr 90px', gap: 10, alignItems: 'center', background: 'transparent', border: 0, padding: 0, cursor: 'pointer', textAlign: 'left', font: 'inherit' }}>
                                        <span style={{ fontWeight: tier === t.tier ? 700 : 400 }}>{t.label}</span>
                                        <span style={{ height: 14, background: '#eef1f6', borderRadius: 3, overflow: 'hidden' }}><span style={{ display: 'block', height: '100%', width: `${100 * t.count / maxTier}%`, background: '#2f5fd0' }} /></span>
                                        <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtN(t.count)} <span style={{ color: '#6d7175' }}>({summary.skus ? Math.round(100 * t.count / summary.skus) : 0}%)</span></span>
                                    </button>
                                ))}
                            </div>
                        </BlockStack>
                    </Card>
                    <Card>
                        <BlockStack gap="200">
                            <Text as="h3" variant="headingSm">🏆 Top 15 — Highest Committed</Text>
                            <Text as="p" tone="subdued">SKUs with the most units committed to open orders — the in-demand items right now.</Text>
                            <div style={{ overflowX: 'auto' }}>
                                <table className="report-table" style={{ width: '100%', borderCollapse: 'collapse', whiteSpace: 'nowrap', fontSize: 13 }}>
                                    <thead><tr><th style={cell}>SKU</th><th style={cell}>Title</th><th style={num}>Committed</th><th style={num}>Available</th><th style={num}>On Hand</th><th style={cell}>Oversell</th></tr></thead>
                                    <tbody>
                                        {panels.top_committed.map(r => (
                                            <tr key={r.sku || r.variant_id} style={{ borderBottom: '1px solid #f1f2f3' }}>
                                                <td style={cell}><SkuLink sku={r.sku} onClick={() => focusSku(r.sku)} /></td>
                                                <td style={titleCell} title={titleOf(r)}>{titleOf(r)}</td>
                                                <td style={{ ...num, background: committedShade(r.committed), fontWeight: 600 }}>{fmtN(r.committed)}</td>
                                                <td style={num}>{fmtN(r.available)}</td><td style={num}>{fmtN(r.on_hand)}</td><td style={cell}>{oversellBadge(r)}</td>
                                            </tr>
                                        ))}
                                        {panels.top_committed.length === 0 && <tr><td colSpan={6} style={{ padding: 16, textAlign: 'center', color: '#6d7175' }}>Nothing committed.</td></tr>}
                                    </tbody>
                                </table>
                            </div>
                        </BlockStack>
                    </Card>
                </InlineGrid>

                <Card padding="0">
                    <div ref={tableRef} style={{ padding: 12, borderBottom: '1px solid #e1e3e5' }}>
                        <BlockStack gap="200">
                            <InlineStack align="space-between" blockAlign="center" wrap>
                                <Text as="h3" variant="headingSm">📋 Full Inventory — {locName || 'location'}</Text>
                                <Text as="span" tone="subdued"><b>{fmtN(data.total)}</b> SKUs{busy ? ' · loading…' : ''}</Text>
                            </InlineStack>
                            <InlineStack gap="300" blockAlign="end" wrap>
                                <ReportPicker label="Vendor" options={vendorOptions} value={vendors} onChange={setVendors} multiple searchable />
                                <ReportPicker label="Stock level" options={TIER_OPTIONS} value={tier} onChange={v => { setTier(v); if (v) setPreset(''); }} />
                                <ReportPicker label="Oversell" options={OVERSELL_OPTIONS} value={oversell} onChange={setOversell} />
                                <ReportPicker label="Preset" options={PRESET_OPTIONS} value={preset} onChange={v => { setPreset(v); if (v) setTier(''); }} />
                            </InlineStack>
                        </BlockStack>
                    </div>
                    <div style={{ overflow: 'auto', maxHeight: '70vh', clipPath: 'inset(0 round 12px)' }}>
                        <table className="report-table" style={{ minWidth: '100%', borderCollapse: 'collapse', whiteSpace: 'nowrap' }}>
                            <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                                <tr>{visibleColumns.map(c => (
                                    <th key={c.key} onClick={() => toggleSort(c.key)} title={c.tip || undefined}
                                        style={{ cursor: 'pointer', textAlign: c.right ? 'right' : 'left', padding: '8px 8px', borderBottom: '1px solid #e1e3e5', whiteSpace: 'nowrap' }}>
                                        {c.label}{sort.key === c.key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                                    </th>))}
                                </tr>
                            </thead>
                            <tbody>
                                {data.rows.map((r, i) => (
                                    <tr key={`${r.variant_id || r.sku}:${i}`} style={{ borderBottom: '1px solid #f1f2f3' }}>
                                        <td style={mono}>{r.sku || '—'}</td>
                                        <td style={cell}>{prodLink(r)}</td>
                                        <td style={cell}>{r.variant_title && r.variant_title !== 'Default Title' ? r.variant_title : ''}</td>
                                        <td style={cell}>{r.vendor || '—'}</td>
                                        <td style={num}>{fmtUSD(r.unit_cost)}</td>
                                        <td style={num}>{fmtN(r.unavailable)}</td>
                                        <td style={num}>{fmtN(r.committed)}</td>
                                        <td style={{ ...num, background: availShade(r.available, 'full'), fontWeight: 600 }}>{fmtN(r.available)}</td>
                                        <td style={num}>{fmtN(r.on_hand)}</td>
                                        <td style={{ ...num, background: r.incoming > 0 ? '#e0f2fb' : 'transparent' }}>{fmtN(r.incoming)}</td>
                                        <td style={num}>{fmtN(r.units_sold)}</td>
                                        {!multiLoc && <td style={mono}>{r.bin || '—'}</td>}
                                        <td style={cell}>{oversellBadge(r)}</td>
                                        {multiLoc && (
                                            <td style={num} title={r.location_names || ''}>{fmtN(r.locations)}</td>
                                        )}
                                    </tr>
                                ))}
                                {!busy && data.rows.length === 0 && (
                                    <tr><td colSpan={visibleColumns.length} style={{ padding: 24, textAlign: 'center', color: '#6d7175' }}>
                                        {lastRun ? 'No SKUs match these filters.' : 'No inventory snapshot yet — run Refresh now or wait for the 4:30 AM ET snapshot.'}
                                    </td></tr>
                                )}
                            </tbody>
                            {data.totals && (
                                <tfoot>
                                    <tr style={{ borderTop: '2px solid #c9cccf', fontWeight: 700 }}>
                                        <td style={cell}>Totals (filtered)</td>
                                        <td style={cell}></td>
                                        <td style={cell}></td>
                                        <td style={cell}></td>
                                        <td style={num}>{fmtUSD(data.totals.inventory_value)}</td>
                                        <td style={num}>{fmtN(data.totals.unavailable)}</td>
                                        <td style={num}>{fmtN(data.totals.committed)}</td>
                                        <td style={num}>{fmtN(data.totals.available)}</td>
                                        <td style={num}>{fmtN(data.totals.on_hand)}</td>
                                        <td style={num}>{fmtN(data.totals.incoming)}</td>
                                        <td style={num}></td>
                                        <td style={cell}></td>
                                        <td style={cell}></td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                    {pages > 1 && (
                        <div style={{ padding: 10, borderTop: '1px solid #e1e3e5' }}>
                            <InlineStack align="space-between" blockAlign="center">
                                <Button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Previous</Button>
                                <Text as="span" tone="subdued">Page {page} of {pages}</Text>
                                <Button disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next →</Button>
                            </InlineStack>
                        </div>
                    )}
                </Card>
            </>)}
        </BlockStack>
    );
}
